/**
 * Flagging categories for different item types
 * @type {Object}
 */
import type * as React from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import type { InputProps } from 'bundles/content-feedback/components/flag/GradingProblemFeedbackEditor';

import _t from 'i18n!nls/content-feedback';

import ItemTypes from './ItemTypes';

const LoadableGradingProblemFeedbackEditor = createLoadableComponent(
  () => import('bundles/content-feedback/components/flag/GradingProblemFeedbackEditor')
);

export class Category {
  id = '';

  label = '';

  example = '';

  isPublic = false;

  declare CustomComponent?: React.ComponentType<InputProps>;

  constructor(id: string, label: string, example?: string, isPublic = false, Component?: $TSFixMe) {
    this.id = id;
    this.label = label;
    this.example = _t('Example: #{example}', { example });
    this.isPublic = !!isPublic;
    this.CustomComponent = Component;
  }

  isHelpful() {
    return this.id === 'helpful';
  }

  // TODO: separate isHelpfulOrConfused from the feedback architecture to avoid changing these to `generic` for API call purposes
  isHelpfulOrConfused() {
    return this.id === 'helpful' || this.id === 'confused';
  }
}

class Content extends Category {
  constructor(example: string) {
    super('content', _t('Content improvement'), example, true);
  }
}

class Grading extends Category {
  constructor(example?: string, Component?: $TSFixMe) {
    super('grading', _t('Grading problems'), example, true, Component);
  }
}

class Offensive extends Category {
  constructor() {
    super(
      'offensive',
      _t('Offensive content'),
      _t('The language used in this video by the interviewer is offensive towards women'),
      true
    );
  }
}

function Categories(isDubbingAvailable?: boolean) {
  return {
    [ItemTypes.Quiz]: [
      new Content(_t('Question 5: would like to have more detailed explanation for wrong answers')),
      new Grading(_t('The quiz fails me even when I got 5 out of 7 questions right.')),
      new Offensive(),
    ],

    [ItemTypes.Reading]: [new Content(_t('Please specify chapters in the references.')), new Offensive()],

    [ItemTypes.Lecture]: [
      new Content(_t('I’d like to see more visuals accompany the bullet points.')),
      new Category('video', _t('Video issues'), _t('Video takes too long to load.')),
      isDubbingAvailable
        ? new Category(
            'audio',
            _t('Audio issues (Including AI dubbing)'),
            _t('Examples: Audio at 2:35 is too low or distorted or AI dubbing at 3:34 has translation errors.')
          )
        : new Category('audio', _t('Audio issues'), _t('Audio at 2:35 breaks up and is hard to understand.')),
      new Offensive(),
      new Category('subtitle', _t('Subtitles issues'), _t('Chinese subtitles are out of sync'), true),
    ],

    [ItemTypes.Peer]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Offensive(),
    ],

    [ItemTypes.Programming]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(undefined, LoadableGradingProblemFeedbackEditor),
      new Offensive(),
    ],

    [ItemTypes.DiscussionPrompt]: [
      new Content(_t("I didn't understand the question. It could be made clearer by...")),
      new Offensive(),
    ],

    [ItemTypes.Notebook]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Offensive(),
    ],

    [ItemTypes.GradedDiscussionPrompt]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
    ],

    [ItemTypes.StaffGraded]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
      new Offensive(),
    ],

    [ItemTypes.TeammateReview]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
    ],

    [ItemTypes.Widget]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
      new Offensive(),
    ],

    [ItemTypes.UngradedWidget]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
      new Offensive(),
    ],

    [ItemTypes.UngradedLab]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
      new Offensive(),
    ],

    [ItemTypes.Workspace]: [],

    [ItemTypes.GradedLti]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
      new Offensive(),
    ],

    [ItemTypes.UngradedLti]: [
      new Content(_t("I didn't understand the instructions. They could be made clearer by...")),
      new Grading(_t('The grading rubric could be improved by...')),
      new Offensive(),
    ],
  };
}

export default Categories;
